import { query, ref as dbRef, orderByChild, limitToLast, endAt, get, onValue, off, type Database } from "firebase/database";
import type { CallRecord } from "~/types/calls";
import { getCallStartEpoch } from "~/utils/calls";

export const MAX_RECORDS = 50;

type RawSummary = {
  state?: string | null;
  stateLastModified?: string | number | Date | null;
  text?: string | null;
};

type RawGraphqlCallRecord = {
  _id?: string | null;
  bpCallDirection?: string | null;
  callType?: string | null;
  callerId?: string | null;
  callerNumber?: string | null;
  destinationNumber?: string | null;
  startEpoch?: number | string | null;
  billSec?: number | null;
  talkTimeSec?: number | null;
  hangupCause?: string | null;
  did?: string | null;
  callees?: CallRecord["callees"];
  recording?: CallRecord["recording"];
  summary?: RawSummary | null;
};

const CALL_RECORD_QUERY = `
  query ($callRecordId: String!) {
    callRecord(query: { _id: $callRecordId }) {
      _id
      bpCallDirection
      callType
      callerId
      callerNumber
      destinationNumber
      startEpoch
      billSec
      talkTimeSec
      hangupCause
      did
      callees {
        uuid
        hangupCause
        talkTimeSec
        callerId
        destinationNumber
        startEpoch
      }
      recording {
        bucket
        objectName
      }
      summary {
        state
        stateLastModified
        text
      }
    }
  }
`;

// Raw dictionary shape from Firebase RTDB snapshot.val()
type RawRecord = Record<string, unknown>;

/**
 * Parses raw Firebase RTDB values into a sorted `CallRecord[]` (newest first).
 */
function mapSnapshot(data: Record<string, RawRecord> | null): CallRecord[] {
  if (!data) return [];
  return Object.entries(data)
    .map(([id, val]) => ({ id, ...val }) as CallRecord)
    .sort((a, b) => getCallStartEpoch(b) - getCallStartEpoch(a));
}

function toEpochSeconds(value: string | number | Date | null | undefined): number | undefined {
  if (value == null) return undefined;
  if (typeof value === "number") {
    return value > 1_000_000_000_000 ? Math.floor(value / 1000) : Math.floor(value);
  }
  if (value instanceof Date) {
    return Math.floor(value.getTime() / 1000);
  }
  const parsedNumber = Number(value);
  if (Number.isFinite(parsedNumber)) {
    return parsedNumber > 1_000_000_000_000 ? Math.floor(parsedNumber / 1000) : Math.floor(parsedNumber);
  }
  const parsedDate = Date.parse(value);
  if (Number.isFinite(parsedDate)) {
    return Math.floor(parsedDate / 1000);
  }
  return undefined;
}

function mapGraphqlCallRecord(record: RawGraphqlCallRecord): CallRecord {
  return {
    id: record._id ?? "",
    bpCallDirection: record.bpCallDirection ?? "outbound",
    callType: record.callType ?? null,
    callerId: record.callerId ?? null,
    callerNumber: record.callerNumber ?? "",
    destinationNumber: record.destinationNumber ?? "",
    startEpoch: toEpochSeconds(record.startEpoch) ?? 0,
    talkTimeSec: record.talkTimeSec ?? null,
    billSec: record.billSec ?? null,
    hangupCause: record.hangupCause ?? null,
    hangupCauseReason: null,
    did: record.did ?? null,
    callees: record.callees ?? null,
    summary: record.summary
      ? {
          state: record.summary.state ?? undefined,
          s: record.summary.state ?? undefined,
          ts: toEpochSeconds(record.summary.stateLastModified),
          text: record.summary.text ?? undefined,
        }
      : null,
    recording: record.recording ?? null,
  };
}

/**
 * Fetches a single page of call records ordered by `startEpoch`.
 * Uses `endBeforeEpoch` as the cursor for pagination (exclusive upper bound).
 */
export async function fetchCallRecordsPage(userId: string, endBeforeEpoch?: number, limit = MAX_RECORDS): Promise<CallRecord[]> {
  const { $rtdb } = useNuxtApp();
  if (!$rtdb) throw new Error("RTDB not initialized");
  const db = $rtdb as unknown as Database;

  const recordsRef = dbRef(db, `callRecords/${userId}/records`);
  const q = endBeforeEpoch
    ? query(recordsRef, orderByChild('startEpoch'), endAt(endBeforeEpoch - 1), limitToLast(limit))
    : query(recordsRef, orderByChild('startEpoch'), limitToLast(limit));

  const snapshot = await get(q);
  return mapSnapshot(snapshot.val() as Record<string, RawRecord> | null);
}

/**
 * Fetches a canonical call record by id from the GraphQL API.
 * This returns richer summary data than the paged RTDB list payload.
 */
export async function fetchCallRecord(callId: string): Promise<CallRecord> {
  const { $gqlRequest } = useNuxtApp() as ReturnType<typeof useNuxtApp> & {
    $gqlRequest: <T>(query: string, variables?: Record<string, unknown>) => Promise<T>;
  };

  const result = await $gqlRequest<{ callRecord: RawGraphqlCallRecord }>(CALL_RECORD_QUERY, {
    callRecordId: callId,
  });

  if (!result?.callRecord) {
    throw new Error("Call record not found");
  }

  return mapGraphqlCallRecord(result.callRecord);
}

/**
 * Subscribes to real-time updates of the latest call records.
 * Returns an unsubscribe function. Used on the Dashboard tab.
 */
export function watchCallRecords(userId: string, callback: (records: CallRecord[]) => void) {
  const { $rtdb } = useNuxtApp();
  if (!$rtdb) throw new Error("RTDB not initialized");
  const db = $rtdb as unknown as Database;

  const recordsRef = dbRef(db, `callRecords/${userId}/records`);
  const q = query(recordsRef, orderByChild('startEpoch'), limitToLast(10));

  onValue(q, (snapshot) => {
    callback(mapSnapshot(snapshot.val() as Record<string, RawRecord> | null));
  });

  return () => off(q);
}
